import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbDropdownModule, NgbModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { I18NextPipe } from 'angular-i18next';

import { ButtonComponent, ModalComponent, ModalFooterDirective, SelectComponent } from 'mbs-ui-kit';

import { RmmDateTimeFormatModule } from 'mbs-ui-shared@components/date-time-format/rmm-date-time-format.module';
import { LicensesPipesModule } from 'mbs-ui-shared@components/licenses/pipes/licenses-pipes.module';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { AdministratorLicense } from 'mbs-ui-shared@models/License';
import { SmartSearchTemplatesModule } from 'mbs-ui-shared@modules/smart-search-templates/smart-search-templates.module';
import { UtilsModule } from 'mbs-ui-shared@utils/utils.module';
import { Observable, catchError, of, switchMap, take } from 'rxjs';

@Component({
  selector: 'mbs-manage-license-admin-modal',
  templateUrl: './manage-license-admin-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    FormsModule,
    NgbModule,
    NgbTooltipModule,
    NgSelectModule,
    ReactiveFormsModule,
    UtilsModule,
    RouterModule,
    SmartSearchTemplatesModule,
    LicensesPipesModule,
    RmmDateTimeFormatModule,
    ButtonComponent,
    ModalComponent,
    SelectComponent,
    I18NextPipe,
    ModalFooterDirective
  ]
})
export class ManageLicenseAdminModalComponent implements AfterViewInit {
  private licensesFacade = inject(LicensesFacade);

  public loading = false;
  public selectedLicenseType;
  public isAvailableLicensesLoading = true;
  public licensesTypes: Observable<Array<AdministratorLicense>> = of(null);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  manage(): void {
    this.loading = true;

    this.licensesFacade
      .grantLicense(this.selectedLicenseType, this.baseModal.data?.entityId, this.selectedLicenseType !== null)
      .pipe(
        take(1),
        catchError(() => {
          this.loading = false;
          return of(false);
        })
      )
      .subscribe(() => {
        this.baseModal.save();
        this.loading = false;
      });
  }

  ngAfterViewInit(): void {
    this.setLicensesTypes();
  }

  setLicensesTypes() {
    this.isAvailableLicensesLoading = true;
    const typesFilter = this.baseModal.data.licenseTypesFilter;
    const licensesFilter: (id, second) => Observable<Array<AdministratorLicense>> = this.baseModal.data.filterLicensesFunc$;
    this.licensesTypes = this.licensesFacade.getAvailableLicenseSummaries().pipe(
      catchError(() => of(null)),
      switchMap((licenses) => {
        this.isAvailableLicensesLoading = false;
        if (licenses) {
          if (typesFilter && typesFilter.length > 0) {
            licenses = licenses.filter((x) => typesFilter.includes(x.licenseType));
          }

          this.setSelectedLicenseType(licenses);

          if (licensesFilter) return licensesFilter(this.baseModal.data?.entityId, [...licenses]);
          return of(licenses);
        } else {
          return of([]);
        }
      })
    );
  }

  private setSelectedLicenseType(licenses: Array<AdministratorLicense>) {
    const currentLicenseType = this.baseModal.data.licenseTypeId;
    if (currentLicenseType) {
      const foundLicenseType = licenses.find((license) => license.licenseType === currentLicenseType);
      if (foundLicenseType) {
        this.selectedLicenseType = currentLicenseType;
      }
    }
  }
}
